import { useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useStaffReport } from '@/application/usecases/useBoard';
import { useUsers } from '@/application/usecases/useAdmin';
import { useSession } from '@/application/usecases/useSession';
import { formatDuration } from '@/application/timer/timerStore';
import type {
  CustomPeriod,
  ReportRange,
  StaffPeriod,
  StaffProductivityRow,
} from '@/application/ports/ReportRepository';
import { DEPARTMENT_LABEL } from '@/domain';
import { initials } from '@/shared/format/initials';
import { formatDay, formatDaySpan } from '@/shared/format/instant';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { SkeletonCards } from '@/ui/feedback/Loading';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { useListMotion } from '@/ui/motion/useListMotion';
import {
  CalendarIcon,
  ChartIcon,
  ChevronIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentIcon,
  QueueIcon,
  SortIcon,
  StaffIcon,
  TargetIcon,
  ToothIcon,
} from '@/ui/icons';
import styles from './StaffScreen.module.css';

// All time first, and chosen when the screen opens: the lab's request of 17 October 2026, item 4.
const RANGES: ReadonlyArray<{ key: ReportRange; label: string }> = [
  { key: 'all', label: strings.reports.rangeAll },
  { key: 'today', label: strings.reports.rangeToday },
  { key: 'week', label: strings.reports.rangeWeek },
  { key: 'month', label: strings.reports.rangeMonth },
];

/** What a card can be sorted by: the same figures the table's headers sorted. */
type SortKey =
  | 'name'
  | 'tasksCompleted'
  | 'teethWorked'
  | 'jobOrdersCreated'
  | 'activeSeconds'
  | 'averageSecondsPerTask'
  | 'onTimePercent'
  | 'utilisationPercent';

const SORTS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'name', label: strings.staff.colTechnician },
  { key: 'tasksCompleted', label: strings.staff.colTasks },
  { key: 'teethWorked', label: strings.staff.colTeeth },
  { key: 'jobOrdersCreated', label: strings.staff.colCreated },
  { key: 'activeSeconds', label: strings.staff.colTime },
  { key: 'averageSecondsPerTask', label: strings.staff.colAverage },
  { key: 'onTimePercent', label: strings.staff.colOnTime },
  { key: 'utilisationPercent', label: strings.staff.colUtilisation },
];

interface Sort {
  key: SortKey;
  direction: 'asc' | 'desc';
}

function sortValue(person: StaffProductivityRow, key: SortKey): string | number {
  return person[key];
}

/**
 * Staff productivity. Section 5.2, redrawn as a card per person by the request
 * of 4 October 2026: the period first, then each person with their figures in
 * a grid of tiles and their utilisation across the foot.
 *
 * Management only, and the server refuses it to `tech` regardless of what the
 * menu shows: technicians never see performance figures, their own or anyone
 * else's. Section 7.10 item 3, and item 6 of section 17.
 *
 * The station comes from the user list rather than the report, because the
 * report answers "how much work" and the roster answers "who and where".
 */
export function StaffScreen() {
  const { data: session } = useSession();
  const [range, setRange] = useState<ReportRange>('all');
  // An exact period outranks the range while it is set. Request of 4 October 2026.
  const [custom, setCustom] = useState<CustomPeriod | null>(null);
  const [choosing, setChoosing] = useState(false);
  const period: StaffPeriod = custom ?? range;
  const report = useStaffReport(period);
  const users = useUsers();
  const [sort, setSort] = useState<Sort | null>(null);
  const sortId = useId();

  const stationOf = new Map(
    (users.data?.items ?? []).map((user) => [user.id, DEPARTMENT_LABEL[user.department]]),
  );

  const rows = useMemo(() => {
    const given = report.data?.rows ?? [];
    if (sort === null) return given;
    const { key, direction } = sort;
    return [...given].sort((a, b) => {
      const left = sortValue(a, key);
      const right = sortValue(b, key);
      const result =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right));
      return direction === 'asc' ? result : -result;
    });
  }, [report.data, sort]);

  const sortLabel =
    sort === null
      ? strings.table.sortDefault
      : `${SORTS.find((option) => option.key === sort.key)?.label ?? ''} ${
          sort.direction === 'asc' ? strings.table.ascending : strings.table.descending
        }`;

  /*
   * Anybody's profile opens for a holder of admin, which is what the server
   * asks; a card is a button to it, else a card. It read the role, so a manager
   * denied admin was offered a profile that was then refused, and somebody
   * given admin was not offered one. The lab's request of 11 October 2026.
   */
  const opensProfile = session?.permissions.includes('admin') === true;

  const list = useListMotion<HTMLDivElement>(rows.map((person) => person.userId));

  return (
    <div className={styles.screen}>
      <p className={styles.hint}>{strings.staff.hint}</p>

      <div className={styles.seg} role="group" aria-label={strings.reports.rangeLabel}>
        {RANGES.map((option) => {
          const on = custom === null && range === option.key;
          return (
            <button
              key={option.key}
              type="button"
              className={on ? styles.on : undefined}
              aria-pressed={on}
              onClick={() => {
                setCustom(null);
                setRange(option.key);
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={`${styles.control} ${custom === null ? '' : styles.controlOn}`}
        aria-pressed={custom !== null}
        onClick={() => setChoosing(true)}
      >
        <span className={styles.controlIcon}>
          <CalendarIcon />
        </span>
        <span className={styles.controlText}>
          {custom === null ? (
            <span className={styles.controlValue}>{strings.staff.customDate}</span>
          ) : (
            <>
              <span className={styles.controlLabel}>{strings.staff.customDate}</span>
              <span className={styles.controlValue}>{formatDaySpan(custom.from, custom.to)}</span>
            </>
          )}
        </span>
        <span className={styles.controlEnd}>
          <ChevronRightIcon />
        </span>
      </button>

      <div className={`${styles.control} ${styles.sortCard}`}>
        <span className={styles.controlIcon}>
          <SortIcon />
        </span>
        <span className={styles.controlText}>
          <label className={styles.controlLabel} htmlFor={sortId}>
            {strings.staff.sortBy}
          </label>
          <span className={styles.controlValue} aria-hidden="true">
            {sortLabel}
          </span>
        </span>
        <span className={styles.controlEnd}>
          <ChevronIcon />
        </span>
        {/* The native picker, over the whole card, so the card is the control. */}
        <select
          id={sortId}
          className={styles.sortSelect}
          value={sort === null ? '' : `${sort.key}:${sort.direction}`}
          onChange={(event) => {
            const [key, direction] = event.target.value.split(':');
            const chosen = SORTS.find((option) => option.key === key);
            if (chosen === undefined) setSort(null);
            else setSort({ key: chosen.key, direction: direction === 'desc' ? 'desc' : 'asc' });
          }}
        >
          <option value="">{strings.table.sortDefault}</option>
          {SORTS.map((option) => (
            <optgroup key={option.key} label={option.label}>
              <option value={`${option.key}:asc`}>
                {option.label} {strings.table.ascending}
              </option>
              <option value={`${option.key}:desc`}>
                {option.label} {strings.table.descending}
              </option>
            </optgroup>
          ))}
        </select>
      </div>

      {report.isError && <p className={styles.hint}>{failureWords(report.error, strings.errors.server)}</p>}
      {report.isPending && <SkeletonCards cards={3} label={strings.common.loadingUsers} />}

      {!report.isPending && !report.isError && rows.length === 0 && (
        <EmptyState
          icon={<StaffIcon />}
          heading={strings.staff.empty}
          detail={strings.staff.emptyDetail}
        />
      )}

      {!report.isPending && !report.isError && rows.length > 0 && (
        <div ref={list} className={styles.people}>
          {rows.map((person) => (
            <PersonCard
              key={person.userId}
              person={person}
              station={stationOf.get(person.userId) ?? ''}
              profile={opensProfile ? `/profile/${person.userId}` : null}
            />
          ))}
        </div>
      )}

      {choosing && (
        <PeriodSheet
          current={custom}
          onClose={() => setChoosing(false)}
          onChoose={(next) => {
            setCustom(next);
            setChoosing(false);
          }}
        />
      )}
    </div>
  );
}

function PersonCard({
  person,
  station,
  profile,
}: {
  person: StaffProductivityRow;
  station: string;
  profile: string | null;
}) {
  const head = (
    <>
      <span className={styles.avatar} aria-hidden="true">
        {initials(person.name)}
      </span>
      <span className={styles.who}>
        <span className={styles.name}>{person.name}</span>
        {station !== '' && (
          <>
            <span className={styles.stationLabel}>{strings.staff.colStation}</span>
            <span className={styles.station}>{station}</span>
          </>
        )}
      </span>
      {profile !== null && (
        <span className={styles.go}>
          <ChevronRightIcon />
        </span>
      )}
    </>
  );

  return (
    <article className={styles.card} aria-label={person.name} data-motion-key={person.userId}>
      {profile !== null ? (
        <Link to={profile} className={styles.head} aria-label={`${strings.staff.openProfile}: ${person.name}`}>
          {head}
        </Link>
      ) : (
        <div className={styles.head}>{head}</div>
      )}

      <dl className={styles.grid}>
        <Metric icon={<QueueIcon />} label={strings.staff.colTasks}>
          {bench(person, person.tasksCompleted)}
        </Metric>
        <Metric icon={<ToothIcon />} label={strings.staff.colTeeth}>
          {bench(person, person.teethWorked)}
        </Metric>
        <Metric icon={<DocumentIcon />} label={strings.staff.colCreated}>
          {person.jobOrdersCreated}
        </Metric>
        <Metric icon={<ClockIcon />} label={strings.staff.colTime}>
          {bench(person, formatDuration(person.activeSeconds))}
        </Metric>
        <Metric icon={<ChartIcon />} label={strings.staff.colAverage}>
          {bench(person, formatDuration(Math.round(person.averageSecondsPerTask)))}
        </Metric>
        <Metric icon={<TargetIcon />} label={strings.staff.colOnTime}>
          {bench(person, `${person.onTimePercent}%`)}
        </Metric>
        <div className={`${styles.tile} ${styles.wide}`}>
          <span className={styles.tileIcon} aria-hidden="true">
            <ChartIcon />
          </span>
          <div className={styles.tileText}>
            <dt className={styles.tileLabel}>{strings.staff.colUtilisation}</dt>
            <dd className={styles.tileValue}>
              {person.benchWork ? (
                <Utilisation percent={person.utilisationPercent} />
              ) : (
                bench(person, null)
              )}
            </dd>
          </div>
        </div>
      </dl>
    </article>
  );
}

function Metric({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className={styles.tile}>
      <span className={styles.tileIcon} aria-hidden="true">
        {icon}
      </span>
      <div className={styles.tileText}>
        <dt className={styles.tileLabel}>{label}</dt>
        <dd className={styles.tileValue}>{children}</dd>
      </div>
    </div>
  );
}

/**
 * A bench figure, or a dash for somebody on the report only for the job orders
 * they created, whose zero hours would read as idleness. The station above
 * already says reception or management; a screen reader is told in words.
 * Request of 3 October 2026, section 4.
 */
function bench(person: StaffProductivityRow, value: ReactNode): ReactNode {
  return person.benchWork ? (
    value
  ) : (
    <span className={styles.na}>
      <span aria-hidden="true">{'\u2013'}</span>
      <span className="visually-hidden">{strings.staff.notAtBench}</span>
    </span>
  );
}

export function Utilisation({ percent }: { percent: number }) {
  return (
    <span className={styles.util}>
      <span className={styles.bar}>
        <i style={{ width: `${Math.min(100, percent)}%` }} />
      </span>
      <span className={styles.percent}>{percent}%</span>
    </span>
  );
}

/**
 * The exact period: a first and a last lab day, both counted. One day alone is
 * that day. Request of 4 October 2026, section 4.
 */
function PeriodSheet({
  current,
  onClose,
  onChoose,
}: {
  current: CustomPeriod | null;
  onClose: () => void;
  onChoose: (period: CustomPeriod | null) => void;
}) {
  const today = formatDay(new Date().toISOString());
  const [from, setFrom] = useState(current?.from ?? '');
  const [to, setTo] = useState(current?.to ?? '');
  const [error, setError] = useState<string | null>(null);
  const ids = { from: useId(), to: useId() };

  function apply() {
    const first = from !== '' ? from : to;
    const last = to !== '' ? to : first;
    if (first === '') {
      setError(strings.staff.customNeedsDay);
      return;
    }
    onChoose(first <= last ? { from: first, to: last } : { from: last, to: first });
  }

  return (
    <BottomSheet
      title={strings.staff.customTitle}
      lead={strings.staff.customLead}
      onClose={onClose}
      closeLabel={strings.sheet.close}
      footer={
        <div className={sheetStyles.actions}>
          {current !== null && <Button onClick={() => onChoose(null)}>{strings.staff.customClear}</Button>}
          <Button variant="primary" className={sheetStyles.primary} onClick={apply}>
            {strings.staff.customApply}
          </Button>
        </div>
      }
    >
      <div className={styles.dates}>
        <div className={styles.date}>
          <label htmlFor={ids.from}>{strings.staff.customFrom}</label>
          <input
            id={ids.from}
            type="date"
            max={today}
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setError(null);
            }}
          />
        </div>
        <div className={styles.date}>
          <label htmlFor={ids.to}>{strings.staff.customTo}</label>
          <input
            id={ids.to}
            type="date"
            max={today}
            min={from === '' ? undefined : from}
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setError(null);
            }}
          />
        </div>
      </div>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </BottomSheet>
  );
}
